import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import ExportMenu from "./ExportButton";
import { exportPath } from "../../lib/exportFile";
import {
  Loader2, ChevronRight, MapPin, Users, GraduationCap,
  Building2, BookOpen, Layers, Megaphone, GitBranch, ArrowLeft, BarChart3,
  CalendarRange, Sunrise, Handshake, Target,
} from "lucide-react";

/**
 * Analytics Dashboard v1 — Geographic & Enrollment Intelligence.
 *
 * Sections (per spec):
 *   1. Overview (KPI cards + period filter)
 *   2. Counties — bar chart sorted desc + click → drill-down
 *   3. County drill-down — KPIs + cities table
 *   4. România heat grid — shaded tile per county with tooltip
 *   5. Top 20 localities
 *   6. Top schools (parteneriate)
 *   7. Disciplines distribution (visualised as proportional bars)
 *   8. Teaching levels (bar chart)
 *   9. Acquisition sources (table — leads + enrollments)
 *  10. Interest volume (M920): visitors → leads → preregistrations → payments
 *      → participants, displayed as independent signals (NO artificial
 *      conversion ratio between leads and enrollments — they are parallel
 *      interest channels, not sequential funnel stages).
 *
 * All sections support CSV export (where the spec calls for tabular data)
 * and respect a single global period filter (today | 7d | 30d | 90d | all).
 */

const PERIODS = [
  { value: "today", label: "Astăzi" },
  { value: "7d",    label: "Ultimele 7 zile" },
  { value: "30d",   label: "Ultimele 30 zile" },
  { value: "90d",   label: "Ultimele 90 zile" },
  { value: "all",   label: "Tot timpul" },
];

// Stable reference so `data?.X ?? EMPTY_ARRAY` does not produce a new array
// on every render (which would invalidate `useMemo` dependency arrays).
const EMPTY_ARRAY = Object.freeze([]);

export default function AnalyticsTab() {
  const [period, setPeriod] = useState("30d");
  const [drilldownCounty, setDrilldownCounty] = useState(null);

  return (
    <div className="clarity-mask space-y-6" data-clarity-mask="true" data-testid="analytics-tab">
      <PeriodHeader period={period} onChange={setPeriod} />

      {drilldownCounty ? (
        <CountyDrilldown
          countyName={drilldownCounty}
          period={period}
          onBack={() => setDrilldownCounty(null)}
        />
      ) : (
        <>
          <OverviewSection period={period} />
          <CountiesSection period={period} onSelectCounty={setDrilldownCounty} />
          <HeatmapSection period={period} onSelectCounty={setDrilldownCounty} />
          <TopCitiesSection period={period} />
          <TopSchoolsSection period={period} />
          <DisciplinesSection period={period} />
          <LevelsSection period={period} />
          {/* M918 — Availability + UTM analytics. */}
          <AvailabilityPeriodSection period={period} />
          <AvailabilityTimeOfDaySection period={period} />
          <SourcesSection period={period} />
          <CampaignsSection period={period} />
          <PartnersSection period={period} />
          <InterestVolumeSection period={period} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Period filter (sticky-style header)
// ---------------------------------------------------------------------------

function PeriodHeader({ period, onChange }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
      <div className="flex items-center gap-2 text-[#373A45]">
        <BarChart3 size={18} className="text-[#965EA7]" />
        <span className="font-display text-base font-semibold">Analytics</span>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="analytics-period-filter">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => onChange(p.value)}
            data-testid={`analytics-period-${p.value}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              period === p.value
                ? "bg-[#965EA7] text-white border-[#965EA7]"
                : "bg-white text-[#373A45] border-[#E5E7EB] hover:border-[#965EA7]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic data hook + helpers
// ---------------------------------------------------------------------------

function useAnalyticsEndpoint(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(path)
      .then((r) => { if (!cancelled) { setData(r.data); setError(null); } })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.detail || err.message || "Eroare";
        setError(msg);
        setData(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);
  return { data, loading, error };
}

function exportCsv(section, period, format) {
  // Use a regular browser navigation so cookies are sent + browser handles the
  // file download dialog. The endpoint names the file and says its type.
  window.open(`${API}${exportPath("/admin/analytics/export.csv", { section, period }, format)}`, "_blank", "noopener");
  toast.success("Export pornit");
}

function SectionCard({ title, icon: Icon, action, children, testid }) {
  return (
    <section
      className="bg-white rounded-2xl border border-[#E5E7EB] p-5 md:p-6"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} className="text-[#965EA7]" />}
          <h3 className="font-display text-base md:text-lg font-semibold text-[#373A45]">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function ExportButton({ section, period }) {
  return (
    <ExportMenu
      onExport={(format) => exportCsv(section, period, format)}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#965EA7] hover:bg-[#F0E7F4] rounded-full px-3 py-1.5 transition-colors"
      testid={`analytics-export-${section}`}
    />
  );
}

function LoadingBlock({ rows = 3 }) {
  return (
    <div className="space-y-2 animate-pulse" data-testid="analytics-loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-8 bg-[#F5F6F6] rounded" />
      ))}
    </div>
  );
}

function ErrorBlock({ message }) {
  return (
    <div className="rounded-xl border border-[#E63946]/30 bg-[#E63946]/5 p-4 text-sm text-[#9b1f2c]" data-testid="analytics-error">
      {message}
    </div>
  );
}

function EmptyBlock({ children = "Nu există date pentru perioada selectată." }) {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] p-6 text-sm text-[#6B7280] text-center" data-testid="analytics-empty">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1 — Overview KPI cards
// ---------------------------------------------------------------------------

function OverviewSection({ period }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/overview?period=${period}`);
  if (loading) return <LoadingBlock rows={2} />;
  if (error) return <ErrorBlock message={error} />;
  const k = data?.kpis || {};
  const cards = [
    { label: "Total Lead-uri",         value: k.total_leads,        accent: "purple" },
    { label: "Total Preînscrieri",     value: k.total_enrollments,  accent: "green"  },
    { label: "Total Contacte",         value: k.total_contacts,     accent: "blue",
      hint: "Lead-uri + Preînscrieri" },
    { label: "Consent marketing",      value: `${k.marketing_consent_pct ?? 0}%`,
      hint: `${k.marketing_consent_yes ?? 0}/${k.leads_period ?? 0}`,         accent: "blue" },
    { label: "Lead-uri (30 zile)",     value: k.leads_30d,          accent: "neutral" },
    { label: "Preînscrieri (30 zile)", value: k.enrollments_30d,    accent: "neutral" },
    { label: "Județe active",          value: k.active_counties,    accent: "neutral" },
    { label: "Localități active",      value: k.active_cities,      accent: "neutral" },
    { label: "Școli unice",            value: k.unique_schools,     accent: "neutral" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="analytics-overview">
      {cards.map((c, i) => (
        <div key={i} className={`rounded-2xl p-4 border ${ACCENTS[c.accent]}`} data-testid={`analytics-kpi-${i}`}>
          <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{c.label}</div>
          <div className="font-display text-2xl font-bold mt-1">{c.value ?? "—"}</div>
          {c.hint && <div className="text-[10px] mt-0.5 opacity-70">{c.hint}</div>}
        </div>
      ))}
    </div>
  );
}

const ACCENTS = {
  purple:  "bg-[#F0E7F4] border-[#965EA7]/30 text-[#7A4986]",
  green:   "bg-[#E7F2EB] border-[#519872]/30 text-[#2F6B4E]",
  blue:    "bg-[#E5EEF5] border-[#3F8EBA]/30 text-[#1f5d80]",
  neutral: "bg-white border-[#E5E7EB] text-[#373A45]",
};

// ---------------------------------------------------------------------------
// 2 — Counties bar chart
// ---------------------------------------------------------------------------

function CountiesSection({ period, onSelectCounty }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/counties?period=${period}`);
  const rows = data?.rows ?? EMPTY_ARRAY;
  const max = useMemo(
    () => Math.max(1, ...rows.map((r) => Math.max(r.leads || 0, r.enrollments || 0))),
    [rows],
  );

  return (
    <SectionCard
      title="Distribuție pe județe"
      icon={MapPin}
      action={<ExportButton section="counties" period={period} />}
      testid="analytics-counties"
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (rows.length === 0 ? <EmptyBlock /> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => onSelectCounty(r.value)}
              data-testid={`analytics-county-row-${r.value}`}
              className="w-full grid grid-cols-12 items-center gap-2 hover:bg-[#F5F6F6] rounded-lg px-2 py-2 transition-colors text-left"
            >
              <div className="col-span-3 md:col-span-2 text-sm font-semibold text-[#373A45]">{r.value}</div>
              <div className="col-span-6 md:col-span-7">
                <div className="flex items-center gap-1">
                  <div className="h-2 bg-[#965EA7] rounded-full" style={{ width: `${(r.leads / max) * 100}%`, minWidth: r.leads ? "4px" : 0 }} />
                  <div className="h-2 bg-[#519872] rounded-full" style={{ width: `${(r.enrollments / max) * 100}%`, minWidth: r.enrollments ? "4px" : 0 }} />
                </div>
              </div>
              <div className="col-span-3 md:col-span-3 grid grid-cols-2 text-right text-xs">
                <span className="text-[#965EA7] font-semibold">{r.leads}</span>
                <span className="text-[#519872] font-semibold">{r.enrollments}</span>
              </div>
            </button>
          ))}
          <div className="text-[10px] uppercase tracking-widest text-[#6B7280] mt-3 flex gap-4">
            <span><span className="inline-block w-2 h-2 bg-[#965EA7] rounded-full mr-1" /> Lead-uri</span>
            <span><span className="inline-block w-2 h-2 bg-[#519872] rounded-full mr-1" /> Preînscrieri</span>
            <span className="ml-auto text-[#6B7280] flex items-center gap-1">Click pe un județ pentru detalii <ChevronRight size={12} /></span>
          </div>
        </div>
      ))}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 3 — County drill-down
// ---------------------------------------------------------------------------

function CountyDrilldown({ countyName, period, onBack }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/counties/${encodeURIComponent(countyName)}?period=${period}`);
  return (
    <div className="space-y-4" data-testid="analytics-county-drilldown">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-[#965EA7] hover:underline"
        data-testid="analytics-drilldown-back"
      >
        <ArrowLeft size={14} /> Înapoi la toate județele
      </button>

      <h2 className="font-display text-2xl font-bold text-[#373A45]">
        {countyName.toUpperCase()}
      </h2>

      {loading && <LoadingBlock rows={4} />}
      {error && <ErrorBlock message={error} />}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiTile label="Lead-uri" value={data.kpis.leads} />
            <KpiTile label="Preînscrieri" value={data.kpis.enrollments} />
            <KpiTile label="Localități unice" value={data.kpis.unique_cities} />
            <KpiTile label="Școli unice" value={data.kpis.unique_schools} />
          </div>

          <SectionCard title="Localități" icon={Building2} testid="analytics-drilldown-cities">
            {data.cities.length === 0 ? (
              <EmptyBlock>Nu există localități înregistrate pentru acest județ în perioada selectată.</EmptyBlock>
            ) : (
              <DataTable
                columns={[
                  { key: "value", label: "Localitate", align: "left" },
                  { key: "leads", label: "Lead-uri", align: "right" },
                  { key: "enrollments", label: "Preînscrieri", align: "right" },
                ]}
                rows={data.cities}
                testid="analytics-drilldown-cities-table"
              />
            )}
          </SectionCard>

          {/* M918 — Availability per county drill-down. Helps decide câte
              grupe pot forma și în ce interval / perioadă per județ. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DrilldownAvailabilityBlock
              title="Perioadă preferată"
              icon={CalendarRange}
              rows={data.availability?.period || []}
              testid="analytics-drilldown-availability-period"
            />
            <DrilldownAvailabilityBlock
              title="Interval orar preferat"
              icon={Sunrise}
              rows={data.availability?.time_of_day || []}
              testid="analytics-drilldown-availability-tod"
            />
          </div>
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value }) {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-3">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-[#6B7280]">{label}</div>
      <div className="font-display text-xl font-bold text-[#373A45] mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function DrilldownAvailabilityBlock({ title, icon: Icon, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count || 0));
  return (
    <SectionCard title={title} icon={Icon} testid={testid}>
      {rows.length === 0 ? (
        <EmptyBlock>Niciun răspuns înregistrat încă.</EmptyBlock>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.value} className="grid grid-cols-12 items-center gap-2">
              <div className="col-span-5 text-sm font-semibold text-[#373A45] truncate" title={r.value}>{r.value}</div>
              <div className="col-span-5">
                <div className="h-2.5 bg-[#965EA7] rounded-full" style={{ width: `${(r.count / max) * 100}%`, minWidth: "6px" }} />
              </div>
              <div className="col-span-2 grid grid-cols-2 text-right text-xs">
                <span className="text-[#965EA7] font-semibold tabular-nums">{r.count}</span>
                <span className="text-[#6B7280] tabular-nums">{r.pct}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 4 — Heatmap grid (România)
// ---------------------------------------------------------------------------

function HeatmapSection({ period, onSelectCounty }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/counties?period=${period}`);
  const heatmap = data?.heatmap ?? EMPTY_ARRAY;
  const max = useMemo(() => Math.max(1, ...heatmap.map((c) => c.leads)), [heatmap]);
  return (
    <SectionCard
      title="Hartă România — intensitate cerere per județ"
      icon={MapPin}
      testid="analytics-heatmap"
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (
        <>
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-1.5">
            {heatmap.map((c) => {
              const intensity = c.leads === 0 ? 0 : Math.min(1, 0.15 + (c.leads / max) * 0.85);
              const bg = `rgba(150, 94, 167, ${intensity})`;
              const fg = intensity > 0.55 ? "#fff" : "#373A45";
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => c.leads + c.enrollments > 0 ? onSelectCounty(c.name) : null}
                  title={`${c.name} · ${c.leads} lead-uri · ${c.enrollments} preînscrieri`}
                  data-testid={`analytics-heatmap-tile-${c.code}`}
                  className="aspect-square rounded-lg flex flex-col items-center justify-center text-[10px] font-semibold transition-transform hover:scale-105"
                  style={{ background: c.leads === 0 ? "#F5F6F6" : bg, color: c.leads === 0 ? "#9CA3AF" : fg, border: c.leads === 0 ? "1px solid #E5E7EB" : "none" }}
                >
                  <span>{c.code}</span>
                  {c.leads > 0 && <span className="text-[9px] opacity-90">{c.leads}</span>}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#6B7280]">
            <span>Mai puțin</span>
            <div className="flex gap-1">
              {[0.15, 0.35, 0.55, 0.75, 1].map((a, i) => (
                <div key={i} className="w-4 h-3 rounded" style={{ background: `rgba(150, 94, 167, ${a})` }} />
              ))}
            </div>
            <span>Mai mult</span>
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 5 — Top 20 localități
// ---------------------------------------------------------------------------

function TopCitiesSection({ period }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/cities?period=${period}&limit=20`);
  return (
    <SectionCard
      title="Top 20 localități"
      icon={Building2}
      action={<ExportButton section="cities" period={period} />}
      testid="analytics-top-cities"
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (data?.rows?.length === 0 ? <EmptyBlock /> : (
        <DataTable
          columns={[
            { key: "value", label: "Localitate", align: "left" },
            { key: "county", label: "Județ", align: "left" },
            { key: "leads", label: "Lead-uri", align: "right" },
            { key: "enrollments", label: "Preînscrieri", align: "right" },
          ]}
          rows={data?.rows || []}
          testid="analytics-top-cities-table"
        />
      ))}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 6 — Top schools
// ---------------------------------------------------------------------------

function TopSchoolsSection({ period }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/schools?period=${period}&limit=20`);
  return (
    <SectionCard
      title="Top școli (parteneriate)"
      icon={GraduationCap}
      action={<ExportButton section="schools" period={period} />}
      testid="analytics-top-schools"
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (data?.rows?.length === 0 ? <EmptyBlock /> : (
        <DataTable
          columns={[
            { key: "value", label: "Școală", align: "left" },
            { key: "city", label: "Localitate", align: "left" },
            { key: "county", label: "Județ", align: "left" },
            { key: "leads", label: "Lead-uri", align: "right" },
            { key: "enrollments", label: "Preînscrieri", align: "right" },
          ]}
          rows={data?.rows || []}
          testid="analytics-top-schools-table"
        />
      ))}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 7 — Disciplines
// ---------------------------------------------------------------------------

const DISC_COLORS = ["#965EA7", "#3F8EBA", "#519872", "#E07A5F", "#F2C14E", "#7A4986", "#1f5d80", "#2F6B4E"];

function DisciplinesSection({ period }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/disciplines?period=${period}`);
  const rows = data?.rows ?? EMPTY_ARRAY;
  const total = useMemo(() => rows.reduce((s, r) => s + r.leads + r.enrollments, 0), [rows]);
  return (
    <SectionCard
      title="Distribuție pe discipline"
      icon={BookOpen}
      action={<ExportButton section="disciplines" period={period} />}
      testid="analytics-disciplines"
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (rows.length === 0 ? <EmptyBlock /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ProportionBars rows={rows} totalForPct={total} />
          <DataTable
            columns={[
              { key: "value", label: "Disciplină", align: "left" },
              { key: "leads", label: "Lead-uri", align: "right" },
              { key: "enrollments", label: "Preînscrieri", align: "right" },
            ]}
            rows={rows}
            testid="analytics-disciplines-table"
          />
        </div>
      ))}
    </SectionCard>
  );
}

function ProportionBars({ rows, totalForPct }) {
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => {
        const local = r.leads + r.enrollments;
        const pct = totalForPct ? (local / totalForPct) * 100 : 0;
        return (
          <div key={r.value}>
            <div className="flex justify-between text-xs text-[#373A45] mb-1">
              <span className="font-semibold truncate pr-2">{r.value}</span>
              <span className="text-[#6B7280] shrink-0">{local} · {pct.toFixed(1)}%</span>
            </div>
            <div className="h-2 rounded-full bg-[#F5F6F6] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: DISC_COLORS[i % DISC_COLORS.length] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 8 — Teaching levels
// ---------------------------------------------------------------------------

function LevelsSection({ period }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/levels?period=${period}`);
  const rows = data?.rows ?? EMPTY_ARRAY;
  const max = useMemo(() => Math.max(1, ...rows.map((r) => Math.max(r.leads, r.enrollments))), [rows]);
  return (
    <SectionCard
      title="Distribuție pe cicluri de învățământ"
      icon={Layers}
      action={<ExportButton section="levels" period={period} />}
      testid="analytics-levels"
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (rows.length === 0 ? <EmptyBlock>Nu există date despre cicluri de învățământ pentru perioada selectată.</EmptyBlock> : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.value} className="grid grid-cols-12 items-center gap-2">
              <div className="col-span-3 text-sm font-semibold text-[#373A45]">{r.value}</div>
              <div className="col-span-6">
                <div className="flex items-center gap-1">
                  <div className="h-2.5 bg-[#965EA7] rounded-full" style={{ width: `${(r.leads / max) * 100}%`, minWidth: r.leads ? "6px" : 0 }} />
                  <div className="h-2.5 bg-[#519872] rounded-full" style={{ width: `${(r.enrollments / max) * 100}%`, minWidth: r.enrollments ? "6px" : 0 }} />
                </div>
              </div>
              <div className="col-span-3 grid grid-cols-2 text-right text-xs">
                <span className="text-[#965EA7] font-semibold">{r.leads}</span>
                <span className="text-[#519872] font-semibold">{r.enrollments}</span>
              </div>
            </div>
          ))}
        </div>
      ))}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 9 — Sources
// ---------------------------------------------------------------------------

function SourcesSection({ period }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/sources?period=${period}`);
  return (
    <SectionCard
      title="Surse de achiziție"
      icon={Megaphone}
      action={<ExportButton section="sources" period={period} />}
      testid="analytics-sources"
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (data?.rows?.length === 0 ? <EmptyBlock /> : (
        <DataTable
          columns={[
            { key: "value", label: "Sursă", align: "left" },
            { key: "leads", label: "Lead-uri", align: "right" },
            { key: "enrollments", label: "Preînscrieri", align: "right" },
          ]}
          rows={data?.rows || []}
          testid="analytics-sources-table"
        />
      ))}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 10 — Interes generat (M920: replaces the old artificial Lead→PI funnel)
// ---------------------------------------------------------------------------

function InterestVolumeSection({ period }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/interest_volume?period=${period}`);
  const stages = data?.stages ?? EMPTY_ARRAY;
  const totalContacts = data?.total_contacts ?? 0;
  const realConvAvailable = data?.real_conversion_available ?? false;
  const realConversions = data?.real_lead_conversions ?? 0;
  const max = useMemo(
    () => Math.max(1, ...stages.filter(s => s.available).map((s) => s.count || 0)),
    [stages],
  );
  return (
    <SectionCard
      title="Interes generat — volum semnale per etapă"
      icon={GitBranch}
      testid="analytics-interest-volume"
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-[#965EA7]/30 bg-[#F0E7F4] p-3" data-testid="analytics-interest-total-contacts">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[#7A4986] opacity-70">Total Contacte</div>
              <div className="font-display text-2xl font-bold text-[#7A4986] mt-1">{totalContacts.toLocaleString("ro-RO")}</div>
              <div className="text-[10px] mt-0.5 text-[#7A4986] opacity-70">Lead-uri + Preînscrieri directe</div>
            </div>
            <div className="rounded-xl border border-[#E5E7EB] bg-white p-3" data-testid="analytics-interest-real-conv">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[#6B7280]">Conversii reale Lead → Preînscriere</div>
              <div className="font-display text-2xl font-bold text-[#373A45] mt-1">
                {realConvAvailable ? realConversions.toLocaleString("ro-RO") : "—"}
              </div>
              <div className="text-[10px] mt-0.5 text-[#6B7280]">
                {realConvAvailable
                  ? "Lead-uri convertite explicit din CRM"
                  : "Disponibil când vor exista conversii prin CRM"}
              </div>
            </div>
            <div className="rounded-xl border border-[#E5E7EB] bg-white p-3 text-[11px] text-[#6B7280] leading-relaxed">
              <p>
                Lead-urile și preînscrierile sunt <strong>semnale independente de interes</strong> —
                un vizitator poate completa direct preînscrierea, fără să treacă prin lead.
                De aceea nu mai afișăm o conversie artificială.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {stages.map((s) => {
              const widthPct = s.available && s.count != null ? Math.max(8, (s.count / max) * 100) : 100;
              return (
                <div key={s.key} className="flex items-center gap-3" data-testid={`analytics-interest-stage-${s.key}`}>
                  <div className="w-32 sm:w-40 text-sm font-semibold text-[#373A45] shrink-0">{s.label}</div>
                  <div className="flex-1">
                    <div
                      className={`h-9 rounded-lg flex items-center justify-between px-3 text-sm font-semibold ${
                        s.available
                          ? "bg-gradient-to-r from-[#965EA7]/85 to-[#965EA7] text-white"
                          : "bg-[#F5F6F6] text-[#9CA3AF] border border-dashed border-[#E5E7EB]"
                      }`}
                      style={{ width: `${widthPct}%` }}
                    >
                      <span>{s.available ? (s.count ?? 0).toLocaleString("ro-RO") : "În curând"}</span>
                      {!s.available && <span className="text-[10px] uppercase tracking-widest">pregătit</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-[#6B7280]">
            Etapele „Vizitatori” și „Participanți” vor fi conectate la GA4 și la prezența pe grupe în fazele următoare.
          </p>
        </div>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// M918 — Availability sections (Disponibilitate cursuri)
// ---------------------------------------------------------------------------

function AvailabilitySectionGeneric({ title, icon, csvSection, endpoint, period, testid }) {
  const { data, loading, error } = useAnalyticsEndpoint(endpoint);
  const rows = data?.rows ?? EMPTY_ARRAY;
  const max = useMemo(() => Math.max(1, ...rows.map((r) => r.count || 0)), [rows]);
  return (
    <SectionCard
      title={title}
      icon={icon}
      action={<ExportButton section={csvSection} period={period} />}
      testid={testid}
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (rows.length === 0 ? (
        <EmptyBlock>Nu există preferințe înregistrate pentru perioada selectată.</EmptyBlock>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.value} className="grid grid-cols-12 items-center gap-2" data-testid={`${testid}-row-${r.value}`}>
              <div className="col-span-5 md:col-span-4 text-sm font-semibold text-[#373A45] truncate" title={r.value}>{r.value}</div>
              <div className="col-span-4 md:col-span-6">
                <div className="h-2.5 bg-[#965EA7] rounded-full" style={{ width: `${(r.count / max) * 100}%`, minWidth: "6px" }} />
              </div>
              <div className="col-span-3 md:col-span-2 grid grid-cols-2 text-right text-xs">
                <span className="text-[#965EA7] font-semibold tabular-nums">{r.count}</span>
                <span className="text-[#6B7280] tabular-nums">{r.pct}%</span>
              </div>
            </div>
          ))}
          <p className="text-[11px] text-[#6B7280] mt-3">
            Total răspunsuri: <span className="font-semibold">{data?.total ?? 0}</span> preînscrieri.
          </p>
        </div>
      ))}
    </SectionCard>
  );
}

function AvailabilityPeriodSection({ period }) {
  return (
    <AvailabilitySectionGeneric
      title="Disponibilitate — perioadă / zile preferate"
      icon={CalendarRange}
      csvSection="availability_period"
      endpoint={`/admin/analytics/availability/period?period=${period}`}
      period={period}
      testid="analytics-availability-period"
    />
  );
}

function AvailabilityTimeOfDaySection({ period }) {
  return (
    <AvailabilitySectionGeneric
      title="Disponibilitate — interval orar preferat"
      icon={Sunrise}
      csvSection="availability_time_of_day"
      endpoint={`/admin/analytics/availability/time_of_day?period=${period}`}
      period={period}
      testid="analytics-availability-tod"
    />
  );
}

// ---------------------------------------------------------------------------
// M918 — UTM Campaigns + Logiscool partners
// ---------------------------------------------------------------------------

function CampaignsSection({ period }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/utm/campaigns?period=${period}&limit=20`);
  return (
    <SectionCard
      title="Top campanii (UTM)"
      icon={Target}
      action={<ExportButton section="utm_campaigns" period={period} />}
      testid="analytics-utm-campaigns"
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (data?.rows?.length === 0 ? (
        <EmptyBlock>Nu există campanii UTM în perioada selectată.</EmptyBlock>
      ) : (
        <DataTable
          columns={[
            { key: "value", label: "Campanie", align: "left" },
            { key: "leads", label: "Lead-uri", align: "right" },
            { key: "enrollments", label: "Preînscrieri", align: "right" },
          ]}
          rows={data?.rows || []}
          testid="analytics-utm-campaigns-table"
        />
      ))}
    </SectionCard>
  );
}

function PartnersSection({ period }) {
  const { data, loading, error } = useAnalyticsEndpoint(`/admin/analytics/utm/partners?period=${period}`);
  return (
    <SectionCard
      title="Parteneri Logiscool — Lead-uri & Preînscrieri"
      icon={Handshake}
      action={<ExportButton section="utm_partners" period={period} />}
      testid="analytics-utm-partners"
    >
      {loading && <LoadingBlock />}
      {error && <ErrorBlock message={error} />}
      {!loading && !error && (data?.rows?.length === 0 ? (
        <EmptyBlock>
          Nu există date pentru parteneri Logiscool încă. Adaugă în UTM-uri convenția{" "}
          <code className="px-1 bg-[#F5F6F6] rounded text-xs">logiscool_oradea</code>,{" "}
          <code className="px-1 bg-[#F5F6F6] rounded text-xs">logiscool_cluj</code>,
          etc. (`utm_source` sau `utm_content`).
        </EmptyBlock>
      ) : (
        <DataTable
          columns={[
            { key: "value", label: "Partener", align: "left" },
            { key: "leads", label: "Lead-uri", align: "right" },
            { key: "enrollments", label: "Preînscrieri", align: "right" },
          ]}
          rows={data?.rows || []}
          testid="analytics-utm-partners-table"
        />
      ))}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Reusable data table
// ---------------------------------------------------------------------------

function DataTable({ columns, rows, testid }) {
  return (
    <div className="overflow-x-auto -mx-2" data-testid={testid}>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-[#6B7280] border-b border-[#E5E7EB]">
            {columns.map((c) => (
              <th key={c.key} className={`px-2 py-2 font-semibold ${c.align === "right" ? "text-right" : "text-left"}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#F5F6F6] hover:bg-[#F5F6F6]/60">
              {columns.map((c) => {
                const raw = r[c.key];
                const text = raw == null || raw === "" ? "—" : `${raw}${c.suffix || ""}`;
                return (
                  <td key={c.key} className={`px-2 py-2 ${c.align === "right" ? "text-right tabular-nums text-[#373A45]" : "text-[#373A45]"}`}>
                    {text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
